/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, Fragment } from "react"
import { InputNumber, Switch, Tooltip } from "@duxton-web/ui"
import { MoreInfoOutlined } from "@duxton-web/icons"
import { Form } from "grab-ui"
import classnames from "classnames"
import { BCRSInfo, Modifier } from "types/models/menu"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { useIsBcrsEnabled } from "pages/menu/detail/hooks/useIsBcrsEnabled"

import styles from "./BCRSOptionSection.modules.css"

interface BCRSOptionSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any
  modifier: Modifier
  isAllowEdit?: boolean
  onChange?: () => void
}

const eligibleField = (modifierID: string) => `bcrsEligible[${modifierID}]`
const unitCountField = (modifierID: string) => `bcrsUnitCount[${modifierID}]`

export const BCRSOptionSection: FC<BCRSOptionSectionProps> = ({
  form,
  modifier,
  isAllowEdit = true,
  onChange,
}) => {
  const textMap = useTranslationsMapTyped({
    eligibleLabel: "menu.bcrs.option.eligibleLabel",
    eligibleTooltip: "menu.bcrs.option.eligibleTooltip",
    unitCountLabel: "menu.bcrs.option.unitCountLabel",
    unitCountTooltip: "menu.bcrs.option.unitCountTooltip",
    requiredError: "menu.errors.requiredField",
  })

  const isFlagEnabled = useIsBcrsEnabled()

  if (!isFlagEnabled) {
    return null
  }

  const bcrsInfo: BCRSInfo | undefined = modifier.bcrsInfo
  const initialEligible = !!bcrsInfo?.eligible
  const initialUnitCount = bcrsInfo?.unitCount
  const isFromGrabDB = bcrsInfo?.source === "grab"

  const eligibleValue = form.getFieldValue(eligibleField(modifier.modifierID))
  const isEligible =
    eligibleValue !== undefined ? eligibleValue : initialEligible

  const isEligibleEditable = isAllowEdit && !isFromGrabDB

  const toggleEligible = () => {
    if (!isEligibleEditable) return
    const fieldName = eligibleField(modifier.modifierID)
    const current = !!form.getFieldValue(fieldName)
    form.setFieldsValue({ [fieldName]: !current })
    onChange?.()
  }

  return (
    <Fragment>
      <Form.Item className={styles.switchRow}>
        {form.getFieldDecorator(eligibleField(modifier.modifierID), {
          initialValue: initialEligible,
          valuePropName: "checked",
        })(
          <Switch
            data-testid={`bcrs-option-eligible-switch-${modifier.modifierID}`}
            disabled={!isEligibleEditable}
            onChange={() => onChange?.()}
          />,
        )}
        <span
          className={classnames(
            styles.bcrsLabel,
            isEligibleEditable && styles.bcrsLabelClickable,
          )}
          role="button"
          tabIndex={isEligibleEditable ? 0 : -1}
          onClick={toggleEligible}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              toggleEligible()
            }
          }}
        >
          {textMap.eligibleLabel}
        </span>
        <Tooltip overlay={textMap.eligibleTooltip}>
          <MoreInfoOutlined className={styles.bcrsInfoIcon} />
        </Tooltip>
      </Form.Item>
      {isEligible && (
        <Fragment>
          <Form.Item
            label={
              <span>
                {textMap.unitCountLabel}
                <Tooltip overlay={textMap.unitCountTooltip}>
                  <MoreInfoOutlined className={styles.bcrsInfoIcon} />
                </Tooltip>
              </span>
            }
          >
            {form.getFieldDecorator(unitCountField(modifier.modifierID), {
              initialValue: initialUnitCount,
              rules: [
                {
                  required: true,
                  message: textMap.requiredError,
                },
              ],
            })(
              <InputNumber
                data-testid={`bcrs-option-unit-count-${modifier.modifierID}`}
                min={1}
                precision={0}
                disabled={!isAllowEdit || isFromGrabDB}
                style={{ width: "100%" }}
                onChange={() => onChange?.()}
              />,
            )}
          </Form.Item>
        </Fragment>
      )}
    </Fragment>
  )
}
